import { useCallback, useEffect, useMemo, useState } from "react";

export function useEvergreenCountdown(key = "offerEndAt", durationMs = 4 * 60 * 60 * 1000) {
  const now = () => Date.now();

  // init end time (persisted)
  const [endAt, setEndAt] = useState(() => {
    if (typeof window === "undefined") return now() + durationMs;
    const saved = Number(localStorage.getItem(key));
    const t = isNaN(saved) || saved <= now() ? now() + durationMs : saved;
    localStorage.setItem(key, String(t));
    return t;
  });

  // tick to trigger re-render every second
  const [nowMs, setNowMs] = useState(now());

  useEffect(() => {
    const id = setInterval(() => {
      const remain = endAt - now();
      if (remain <= 0) {
        const next = now() + durationMs;           // reset to 4h
        localStorage.setItem(key, String(next));
        setEndAt(next);
      }
      setNowMs(now());                              // <- forces repaint each second
    }, 1000);
    return () => clearInterval(id);
  }, [endAt, durationMs, key]);

  const reset = useCallback(() => {
    const next = now() + durationMs;
    if (typeof window !== "undefined") localStorage.setItem(key, String(next));
    setEndAt(next);
    setNowMs(now());
  }, [durationMs, key]);

  const display = useMemo(() => {
    const left = Math.max(0, endAt - nowMs);
    const h = Math.floor(left / 3600000);
    const m = Math.floor((left % 3600000) / 60000);
    const s = Math.floor((left % 60000) / 1000);
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(h)}:${pad(m)}:${pad(s)}`;
  }, [endAt, nowMs]);

  return { display, reset };
}
